<template>
  <div>
    <div
      v-if="!isForDetailsPage"
      class="main-content-header">
      <h1
        id="packages_list_header"
        class="mb-0">
        <component :is="isCuratorOrganization ? IconColoredExchange : IconColoredDeal" />
        {{ capitalize(i18n.t(isCuratorOrganization ? 'names.curatedDeal' : 'names.deal', 2)) }}
        <div class="view-type-buttons">
          <div class="btn-group">
            <button
              class="btn btn-default btn-sm btn-no-text"
              :class="{ selected: viewType === DealsListViewType.LIST }"
              @click="viewType = DealsListViewType.LIST">
              <icon-list />
            </button>
            <button
              class="btn btn-default btn-sm btn-no-text"
              :class="{ selected: viewType === DealsListViewType.TILES }"
              @click="viewType = DealsListViewType.TILES">
              <icon-tiles />
            </button>
          </div>
        </div>

        <div
          class="actions"
          style="margin-right: 1rem">
          <email-notification
            v-show="isDemandOrganization"
            :notifications="notifications"
            :active-notifications="activeNotifications" />
        </div>
      </h1>
    </div>

    <div class="deals-page-container details-container">
      <div
        class="deals-table-wrapper"
        :class="{ 'details-view': isForDetailsPage }">
        <!-- DEMAND SIDE CURATED DEALS LIST -->
        <curated-deals-list
          v-if="isReady && isDemandOrganization"
          v-show="hasCuratedDeals"
          ref="curatedDealsListRef"
          :buyer-organization-d-s-ps="buyerOrganizationDSPs"
          :media-owners="mediaOwners"
          :view-type="viewType"
          :sort-direction-options="sortDirectionOptions"
          :demographics="demographics"
          :curators="allCuratorOrganizations"
          :selection-enabled="true"
          :lock-selection="lockCuratedSelection"
          :selected-packages="selectedPackages"
          :active-deal="activeDeal"
          @requested-deal-details="onRequestedDealDetails"
          @loaded="onCuratedDealsListLoaded"
          @selection-changed="selectedPackages = $event" />

        <div
          v-if="!isForDetailsPage && (isSupplyOrganization || isExchange)"
          class="row grid-filters-and-actions">
          <div class="col-12 col-sm-6" />
          <div class="col-12 col-sm-6 grid-actions">
            <button
              v-show="selectedPackages.length > 0"
              name="deleteDeals"
              class="btn btn-danger"
              :disabled="permissions != null && !permissions.canWritePackage"
              @click="isDeletePackagesModalVisible = true">
              {{ capitalize(i18n.t('actions.delete')) }}
            </button>
            <button
              v-show="selectedPackages.filter((x) => x.state === 'D').length > 0"
              name="publishDeals"
              class="btn btn-success"
              :disabled="permissions != null && !permissions.canWritePackage"
              @click="promptForPackagesPublish">
              {{ capitalize(i18n.t('actions.publish')) }}
            </button>
            <button
              v-show="selectedPackages.filter((x) => x.state === 'P').length > 0"
              name="unpublishDeals"
              class="btn btn-danger"
              :disabled="permissions != null && !permissions.canWritePackage"
              @click="promptForPackagesUnpublish">
              {{ capitalize(i18n.t('actions.unpublish')) }}
            </button>
            <button
              name="newDeal"
              :disabled="permissions != null && !permissions.canWritePackage"
              class="btn btn-primary"
              @click="newDeal">
              {{ capitalize(i18n.t('actions.newDeal')) }}
            </button>
          </div>
        </div>

        <!-- CURATOR ORG TABLE ACTIONS -->
        <div
          v-if="isCuratorOrganization"
          class="row grid-filters-and-actions">
          <div class="col-12 grid-actions">
            <entity-actions
              :actions="actions"
              @changed="dispatchAction" />
            <button
              class="btn btn-primary"
              @click="$router.push('/curate/deals/new/edit')">
              {{ capitalize(i18n.t('actions.newCuratedDeal')) }}
            </button>
          </div>
        </div>

        <!-- THIS HEADER IS ONLY VISIBLE WHEN BOTH CURATED AND REGULAR DEALS ARE AVAILABLE -->
        <h2
          v-if="hasCuratedDeals"
          class="mb-0">
          <icon-colored-deal class="mr-2" />
          {{ capitalize(i18n.t('names.publisherDeal', 2)) }}
        </h2>

        <!-- LIST VIEW -->
        <deals-table
          v-if="viewType == DealsListViewType.LIST"
          :packages="packages"
          :media-owners="mediaOwners"
          :buyer-organization-d-s-ps="buyerOrganizationDSPs"
          :organization-external-exchanges="organizationExternalExchanges"
          :organization-custom-fields="organizationCustomFields"
          :fixed-filters="fixedFilters"
          :columns="columns"
          :table-download-columns="tableDownloadColumns"
          :sort-by="sortBy"
          :total-count="totalCount"
          :page-size="pageSize"
          :table-text-search="tableTextSearch"
          :new-deal-route="newDealRoute"
          :is-ready="isReady"
          :is-loading="isLoading"
          :is-sales-planner="isSalesPlanner"
          :is-planner-reserve-campaign-i-d-role="isPlannerReserveCampaignIDRole"
          :is-exchange="isExchange"
          :is-sort-ascending="isSortAscending"
          :is-loading-package-details="isLoadingPackageDetails"
          :is-loading-bidding-metrics="isLoadingBiddingMetrics"
          :previous-table-data="table"
          :selection-enabled="!isLoading"
          :lock-selection="lockPublisherSelection"
          :active-deal="activeDeal"
          @requested-deal-details="onRequestedDealDetails"
          @on-table-loaded="onTableLoaded"
          @on-download="onDownload"
          @on-table-filters-changed="onTableFiltersChanged"
          @selection-changed="selectedPackages = $event" />

        <deals-tiles-table
          v-else-if="viewType == DealsListViewType.TILES"
          :is-ready="isReady"
          :packages="packages"
          :columns="columns"
          :table-download-columns="tableDownloadColumns"
          :is-curator-organization="isCuratorOrganization"
          :fixed-filters="fixedFilters"
          :total-count="totalCount"
          :table-text-search="tableTextSearch"
          :sort-by="sortBy"
          :is-sort-ascending="isSortAscending"
          :is-loading="isLoading"
          :sort-direction="sortDirection"
          :sort-direction-options="sortDirectionOptions"
          :sort-by-options="sortByOptions"
          :page-size="pageSize"
          :demographics="demographics"
          :locations="locations"
          :markets="markets"
          :media-types="mediaTypes"
          :media-owners="mediaOwners"
          :buyer-organization-d-s-ps="buyerOrganizationDSPs"
          :previous-table-data="table"
          :selected-packages="selectedPackages"
          :lock-selection="lockPublisherSelection"
          @page-size-changed="(val) => (pageSize = val)"
          @on-table-loaded="onTableLoaded"
          @on-download="onDownload"
          @filters-changed="onTableFiltersChanged"
          @tile-download-clicked="tileDownloadClicked"
          @sort-direction-changed="(x) => (sortDirection = x)"
          @sort-by-changed="(x) => (sortBy = x)"
          @selection-changed="selectedPackages = $event" />
      </div>
      <side-drawer
        class="drawer-content"
        :is-active="(selectedPackages.length > 0 || activeDeal !== null) && isDemandOrganization">
        <deal-cart
          v-if="selectedPackages.length > 0 && isDemandOrganization"
          :cart="selectedPackages"
          :confirm-button-label="capitalize($t('messages.buyFromDeals'))"
          :warning-messages="warningMessages"
          :active-item="activeDeal"
          :preview-items="true"
          @preview-item="onRequestedDealDetails"
          @confirm-cart="buyFromDeals"
          @clear-cart="clearPackages"
          @remove-deal="removePackage" />
        <transition name="fade">
          <deal-details-pane
            :suppliers="mediaOwners"
            :curators="allCuratorOrganizations"
            :active-deal="activeDeal"
            @clear-active-deal="activeDeal = null" />
        </transition>
      </side-drawer>
    </div>

    <!-- MODALS -->

    <modal
      id="delete_packages_confirmation_modal"
      :visible="isDeletePackagesModalVisible"
      :title="capitalize(i18n.t('actions.deletePackages'))"
      :body="capitalize(i18n.t('messages.confirmDeletePackages'))"
      :button1-caption="capitalize(i18n.t('actions.yes'))"
      :button2-caption="capitalize(i18n.t('actions.no'))"
      :button2-visible="true"
      @confirmed="confirmPackagesDeletion"
      @cancelled="cancelPackagesDeletion" />

    <modal
      id="publish_packages_confirmation_modal"
      :visible="isPublishPackagesModalVisible"
      :title="capitalize(i18n.t(isCuratorOrganization ? 'actions.publishCuratedDeals' : 'actions.publishPackages'))"
      :body="
        capitalize(
          i18n.t(isCuratorOrganization ? 'messages.confirmPublishCuratedDeals' : 'messages.confirmPublishPackages'),
        )
      "
      :button1-caption="capitalize(i18n.t('actions.yes'))"
      :button2-caption="capitalize(i18n.t('actions.no'))"
      :button2-visible="true"
      @confirmed="confirmPackagesPublish"
      @cancelled="cancelPackagesPublish" />

    <modal
      id="unpublish_packages_confirmation_modal"
      :visible="isUnpublishPackagesModalVisible"
      :title="capitalize(i18n.t('actions.unpublishPackages'))"
      :body="capitalize(i18n.t('messages.confirmUnpublishPackages'))"
      :button1-caption="capitalize(i18n.t('actions.yes'))"
      :button2-caption="capitalize(i18n.t('actions.no'))"
      :button2-visible="true"
      @confirmed="confirmPackagesUnpublish"
      @cancelled="cancelPackagesUnpublish" />
  </div>
</template>

<script setup lang="ts">
  import { DateTime } from 'luxon';
  import { capitalize, computed, markRaw, onBeforeMount, onMounted, onUnmounted, Ref, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRouter } from 'vue-router';

  import EntityActions from '@/common/components/controls/EntityActions.vue';
  import Table from '@/common/components/controls/table/Table.vue';
  import TableColumn from '@/common/components/controls/table/TableColumn';
  import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
  import DealCart from '@/common/components/deals/DealCart.vue';
  import DealDetailsPane from '@/common/components/deals/DealDetailsPane.vue';
  import { onDealPreviewChanged } from '@/common/components/deals/DealsHelper';
  import EmailNotification from '@/common/components/notifications/EmailNotification.vue';
  import SideDrawer from '@/common/components/ui/SideDrawer.vue';
  import PurchaseTypesEnum from '@/common/enums/purchase-types.enum';
  import { IconColoredDeal, IconColoredExchange } from '@/common/icons/colored';
  import { IconList, IconPublish, IconTiles, IconUnpublish } from '@/common/icons/monochrome';
  import {
    Demographic,
    FilterLogicalOperator,
    Market,
    MediaType,
    OrganizationCustomField,
    Package,
    PurchaseTypeOptions,
  } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import OrganizationType from '@/common/models/memberships/OrganizationType.enum';
  import CuratorOrganization from '@/common/models/misc-api-models/CuratorOrganization';
  import PackageBuyerOfferedToOptions from '@/common/models/package/PackageBuyerOfferedToOptions';
  import PackageStatus from '@/common/models/package/PackageStatus.enum';
  import Filter from '@/common/models/query-params/Filter';
  import * as FilterComparisonOperator from '@/common/models/query-params/FilterComparisonOperator';
  import FilterGroup from '@/common/models/query-params/FilterGroup';
  import LogicalOperator from '@/common/models/query-params/FilterLogicalOperator.enum';
  import QueryParams from '@/common/models/query-params/QueryParams';
  import OrganizationSubscription from '@/common/models/subscriptions/OrganizationSubscription';
  import GlobalEventBus from '@/GlobalEventBus';
  import GlobalEvents from '@/GlobalEvents';
  import { findPath, toServerDateTimeNoTimezone } from '@/globals';
  import CuratedDealsList from '@/org-app/components/packages/CuratedDealsList.vue';
  import DealsListViewType from '@/org-app/components/packages/DealsListViewType.enum';
  import { ISortBy, ISortDirection } from '@/org-app/components/packages/DealSorting';
  import DealsTable from '@/org-app/components/packages/DealsTable.vue';
  import DealsTilesTable from '@/org-app/components/packages/DealsTilesTable.vue';
  import { useExternalExchanges, useMediaOwners, useMediaTypes } from '@/org-app/services/tanstack/queries';
  import { runQueryInScope, waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import { mapActions, mapGetters, mapOrgAppActions, mapOrgAppGetters, mapOrgAppState } from '@/utils/StateMapping';

  //
  // Props
  //
  interface IProps {
    locations?: Location[];
    markets?: Market[];
    mediaOwners: MediaOwner[];
    demographics: Demographic[];
    organizationType?: string;
    isLoading?: boolean;
    isLoadingBiddingMetrics?: boolean;
    isLoadingPackageDetails?: boolean;
    showPackageBiddingMetrics?: boolean;
    packages: Package[];
    totalCount: number;
    organizationCustomFields?: OrganizationCustomField[];
    isForDetailsPage?: boolean;
    planID?: number;
    showDeliveryStats?: boolean;
  }

  const props = withDefaults(defineProps<IProps>(), {
    locations: () => [],
    markets: () => [],
    organizationType: OrganizationType.DEMAND,
    isLoading: false,
    isLoadingBiddingMetrics: false,
    isLoadingPackageDetails: false,
    showPackageBiddingMetrics: false,
    organizationCustomFields: null,
    isForDetailsPage: false,
    showDeliveryStats: false,
  });

  //
  // Data
  //
  const i18n = useI18n();
  const router = useRouter();
  const filterText: Ref<string> = ref(null);
  const fixedFilters: Ref<Filter[]> = ref([]);
  const hasCuratedDeals = ref(false);
  const isDeletePackagesModalVisible = ref(false);
  const isPublishPackagesModalVisible = ref(false);
  const isReady = ref(false);
  const isUnpublishPackagesModalVisible = ref(false);
  const mediaTypes: Ref<MediaType[]> = ref(null);
  const newDealSubscription: Ref<OrganizationSubscription> = ref(null);
  const pageIndex = ref(1); // Not zero based
  const pageSize = ref(10);
  const selectedPackages: Ref<Package[]> = ref([]);
  const sortBy: Ref<ISortBy> = ref(null);
  const sortDirection: Ref<ISortDirection> = ref(null);
  const tableSortAscending: Ref<boolean> = ref(null);
  const tableSortColumn: Ref<TableColumn> = ref(null);
  const tableTextSearch: Ref<string> = ref('');
  const viewType = ref(DealsListViewType.LIST);
  const lastUsedQueryParams = ref(null);
  const activeDeal = ref<Package>(null);

  // Selection Magic
  const lockPublisherSelection = ref(false);
  const lockCuratedSelection = ref(false);
  // Since we are dealing with the ref of two tables, we're going to have to keep track of which has selections
  const activeSelectionTable = ref(null);

  // DOM refs
  const curatedDealsListRef = ref(null);
  const table = ref(null);

  // State
  const { organizationSettings, ReferenceData } = mapOrgAppState();
  const mediaOwnersQuery = useMediaOwners();
  const externalExchangesQuery = useExternalExchanges();
  const buyerOrganizationDSPs = computed(() => ReferenceData.organizationDSPs);
  const organizationDSPIDs = computed(() => ReferenceData.organizationDSPIDs || []);
  const organizationExternalExchanges = computed(() => externalExchangesQuery.items.value || []);
  const externalOrganizationDSPIDs = computed(() => ReferenceData.externalOrganizationDSPIDs || []);
  const allCuratorOrganizations = computed(
    () => ReferenceData?.curators.map((curator: object) => CuratorOrganization.fromApi(curator)) || [],
  );

  //
  // Getters
  //
  const { permissions, isSalesPlanner, isPlannerReserveCampaignIDRole } = mapGetters();
  const { isSidekickOrganization } = mapOrgAppGetters();

  //
  // Computed
  //
  const tableDownloadColumns = computed(() => {
    const renderedColumns = columns.value
      ? Object.keys(columns.value)
          .map((key) => columns.value[key])
          .filter((c) => c.shouldRenderColumn)
      : [];

    return renderedColumns
      ? Object.keys(renderedColumns).map((c) => {
          const col = { ...renderedColumns[c] };
          col.property = matchColumn(col.property);

          if (col.property === 'mediaOwnerIDs') {
            col.transform = (ids) =>
              ids
                .map((id) => {
                  const mo = props.mediaOwners.find((x) => x.id === id);
                  if (!mo) {
                    return capitalize(i18n.t('names.unknown', 1));
                  }
                  return mo.name;
                })
                .join(',');
          }

          return col;
        })
      : [];
  });

  const isDemandOrganization = computed(() => {
    return props.organizationType === OrganizationType.DEMAND;
  });

  const isSupplyOrganization = computed(() => {
    return props.organizationType === OrganizationType.SUPPLY;
  });

  const isExchange = computed(() => {
    return props.organizationType === OrganizationType.EXCHANGE;
  });

  const isCuratorOrganization = computed(() => {
    return props.organizationType === OrganizationType.CURATOR;
  });

  const columns = computed(() => {
    const columns: Record<string, TableColumn> = {
      link: Package.entityProperties.name.getTableColumn(),
    };

    if (isDemandOrganization.value) {
      columns.mediaOwner = Package.entityProperties.mediaOwner.getTableColumn();
      columns.dealVisibility = Package.entityProperties.dealVisibility.getTableColumn({
        property: 'buyer_access',
        sourceFilterProperty: 'buyer_access',
        shouldRenderColumn: false,
      });
    } else if (isCuratorOrganization.value) {
      columns.co2EmissionsScore = TableColumn.fromObject({
        key: 'co2EmissionsScore',
        label: capitalize(i18n.t('names.co2EmissionsScore')),
        property: 'co2EmissionsScore',
        allowSort: false,
        useAsFilter: false,
      });
    }

    columns.dealIDFormatted = Package.entityProperties.dealIDFormatted.getTableColumn();

    if (isSupplyOrganization.value || isExchange.value) {
      columns.status = Package.entityProperties.status.getTableColumn({
        useAsFilter: isSupplyOrganization.value,
        orderBy: ['state', 'flight_to'],
      });
    } else if (isCuratorOrganization.value) {
      columns.status = Package.entityProperties.status.getTableColumn({
        allowSort: true,
      });
    }
    if (props.showPackageBiddingMetrics) {
      columns.bidRequestSentInTheLastHour = TableColumn.fromObject({
        label: capitalize(i18n.t('messages.bidRequestSentInTheLastHour')),
        allowSort: false,
        useAsFilter: false,
        tableDownloadColumn: 'bidRequestSentInTheLastHourDescription',
      });
      columns.bidResponseInTheLastHour = TableColumn.fromObject({
        label: capitalize(i18n.t('messages.bidResponseInTheLastHour')),
        allowSort: false,
        useAsFilter: false,
        tableDownloadColumn: 'bidResponseInTheLastHourDescription',
      });
      columns.didPlayedInTheLastHour = TableColumn.fromObject({
        label: capitalize(i18n.t('messages.playedInLastHour')),
        allowSort: false,
        useAsFilter: false,
        tableDownloadColumn: 'didPlayedInTheLastHourDescription',
      });
    }

    if (isSupplyOrganization.value) {
      columns.dsp = Package.entityProperties.dsp.getTableColumn({
        allowSort: false,
        useAsFilter: false,
        tableDownloadColumn: 'formattedDSPName',
      });
      columns.buyers = Package.entityProperties.dspBuyers.getTableColumn({
        allowSort: false,
        shouldRenderColumn: false,
        filterOptionsTransformation: (options) => buyerFilterOptionsTransformation(options),
        customQueryParams: (values, comparisonOperator) => buyerCustomQueryParams(values, comparisonOperator),
      });
      columns.exchange = Package.entityProperties.dspExchanges.getTableColumn({
        allowSort: false,
        shouldRenderColumn: false,
        customQueryParams: (values, comparisonOperator) => dspCustomQueryParams(values, comparisonOperator),
      });
      columns.createdBy = Package.entityProperties.createdBy.getTableColumn({
        isDefault: true,
        allowSort: false,
        tableDownloadColumn: 'createdByUser',
      });
    }

    columns.startDateDescription = Package.entityProperties.startDate.getTableColumn({
      orderBy: ['flight_from'],
      tableDownloadColumn: 'startDate',
    });
    columns.endDateDescription = Package.entityProperties.endDate.getTableColumn({
      orderBy: ['flight_to'],
      tableDownloadColumn: 'endDate',
    });
    if (!isCuratorOrganization.value) {
      columns.impressionsDescription = Package.entityProperties.impressionsDescription.getTableColumn({
        useAsFilter: false,
        orderBy: ['market_targeted_impressions_min', 'market_targeted_impressions_max'],
      });
      columns.budgetDescription = Package.entityProperties.budgetDescription.getTableColumn({
        useAsFilter: false,
        orderBy: ['budget_min', 'budget_max'],
      });
    }

    if (props.showDeliveryStats) {
      columns.totalImpressionsFormatted = TableColumn.fromObject({
        key: 'totalImpressionsFormatted',
        type: TableColumnTypes.string,
        label: capitalize(i18n.t('names.impressionDelivered', 2)),
        allowSort: false,
        useAsFilter: false,
        right: true,
      });
      columns.averageCPMFormatted = TableColumn.fromObject({
        key: 'averageCPMFormatted',
        type: TableColumnTypes.string,
        label: capitalize(i18n.t('names.averageCPM')),
        allowSort: false,
        useAsFilter: false,
        right: true,
      });
      columns.totalSpendFormatted = TableColumn.fromObject({
        key: 'totalSpendFormatted',
        type: TableColumnTypes.string,
        label: capitalize(i18n.t('names.adSpend')),
        allowSort: false,
        useAsFilter: false,
        right: true,
      });
    }

    columns.purchaseTypeName = Package.entityProperties.purchaseType.getTableColumn();
    columns.cpmDescription = Package.entityProperties.cpmDescription.getTableColumn({
      useAsFilter: false,
      orderBy: ['cpm_from_rate_card', 'cpm_fixed'],
    });
    if (isCuratorOrganization.value) {
      columns.countries = Package.entityProperties.countries.getTableColumn({
        allowSort: false,
        useAsFilter: false,
      });
      columns.publisherDealID = Package.entityProperties.publisherDealID.getTableColumn({
        allowSort: false,
        shouldRenderColumn: false,
        customQueryParams: (values, comparisonOperator) => curatedDealIDCustomQueryParams(values, comparisonOperator),
      });
    }
    if (!isCuratorOrganization.value) {
      // Filters only
      columns.impressionsMin = Package.entityProperties.impressionsMin.getTableColumn({
        shouldRenderColumn: false,
      });
      columns.impressionsMax = Package.entityProperties.impressionsMax.getTableColumn({
        shouldRenderColumn: false,
      });
      columns.budgetMin = Package.entityProperties.budgetMin.getTableColumn({
        shouldRenderColumn: false,
      });
      columns.budgetMax = Package.entityProperties.budgetMax.getTableColumn({
        shouldRenderColumn: false,
      });
    }
    if (isSupplyOrganization.value) {
      columns.packageProperties = TableColumn.fromObject({
        label: capitalize(i18n.t('names.organizationCustomField', 2)),
      });
      columns.cpmFixed = Package.entityProperties.cpmFixed.getTableColumn({
        shouldRenderColumn: false,
      });
      columns.availableDeals = Package.entityProperties.availableDeals.getTableColumn({
        shouldRenderColumn: false,
      });
    }
    if (organizationSettings?.enablePlansFeature) {
      columns.planID = Package.entityProperties.planID.getTableColumn();
    }
    if (isDemandOrganization.value && organizationDSPIDs.value?.length > 0) {
      columns.offeredTo = Package.entityProperties.offeredTo.getTableColumn({
        shouldRenderColumn: false,
        allowSort: false,
        isFixed: true,
        sendTracking: true,
        source: PackageBuyerOfferedToOptions.options(organizationDSPIDs.value).map((x) => ({
          id: x.value,
          name: x.name,
          filters: x.filters,
        })),
      });
    }

    columns.modifiedOnFormatted = Package.entityProperties.modifiedOnFormatted.getTableColumn();

    return columns;
  });

  const sortByOptions = computed(() => {
    const options: ISortBy[] = [
      {
        label: capitalize(i18n.t('names.name', 1)),
        property: 'name',
      },
      {
        label: capitalize(i18n.t('names.startDate', 1)),
        property: 'start_date',
        orderBy: ['flight_from'],
      },
      {
        label: capitalize(i18n.t('names.endDate', 1)),
        property: 'end_date',
        orderBy: ['flight_to'],
      },
      {
        label: capitalize(i18n.t('names.impression', 2)),
        property: 'impressions',
        orderBy: ['market_targeted_impressions_min', 'market_targeted_impressions_max'],
      },
      {
        label: capitalize(i18n.t('names.budget', 1)),
        property: 'budget',
        orderBy: ['budget_min', 'budget_max'],
      },
      {
        label: capitalize(i18n.t('names.cpm', 1)),
        property: 'cpm',
        orderBy: ['cpm_from_rate_card', 'cpm_fixed'],
      },
    ];

    return options;
  });

  const sortDirectionOptions = computed(() => {
    const options: ISortDirection[] = [
      {
        label: capitalize(i18n.t('options.sortDirection.ascending')),
        value: 'asc',
      },
      {
        label: capitalize(i18n.t('options.sortDirection.descending')),
        value: 'desc',
      },
    ];

    return options;
  });

  const isSortAscending = computed(() => {
    return sortDirection.value?.value === 'asc';
  });

  const hasNewDealSubscription = computed(() => {
    return newDealSubscription.value !== null;
  });

  const notifications = computed(() => {
    return [
      {
        name: capitalize(i18n.t('messages.newPublisherDealAvailable', 2)),
        id: 'new_deal_email_notifications',
        subscribe: subscribeToEmails,
        unsubscribe: unsubscribeToEmails,
      },
    ];
  });

  const activeNotifications = computed(() => {
    return [
      ...(hasNewDealSubscription.value
        ? [notifications.value.find((x) => x.id === 'new_deal_email_notifications')]
        : []),
    ];
  });

  const newDealRoute = computed(() => {
    return isExchange.value ? '/exch/deals/new/edit' : '/deals/new/edit';
  });

  const sidekickDealFilters = computed(() => {
    const { cadenceFilter, pgFilter, ngOtherFilter, preferredOtherFilter } = PurchaseTypeOptions.sidekickDealFilters();
    return new FilterGroup([cadenceFilter, pgFilter, ngOtherFilter, preferredOtherFilter], FilterLogicalOperator.OR);
  });

  const actions = computed(() => {
    const actionPublishCuratedDeals = {
      type: 'publish-curated-deals',
      name: capitalize(i18n.t('actions.publish')),
      icon: markRaw(IconPublish),
      $isDisabled: permissions.value == null || !permissions.value?.canWritePackage,
    };

    const actionUnpublishCuratedDeals = {
      type: 'unpublish-curated-deals',
      name: capitalize(i18n.t('actions.unpublish')),
      icon: markRaw(IconUnpublish),
      $isDisabled: permissions.value == null || !permissions.value?.canWritePackage,
    };

    let actions = [];
    if (isCuratorOrganization.value) {
      actions =
        selectedPackages.value.length > 0
          ? [
              ...(selectedPackages.value.some((x) => x.state === PackageStatus.Draft && !x.hasEnded)
                ? [actionPublishCuratedDeals]
                : []),
              ...(selectedPackages.value.some((x) => x.state === PackageStatus.Published && !x.hasEnded)
                ? [actionUnpublishCuratedDeals]
                : []),
            ]
          : [];
    }
    return actions;
  });

  const warningMessages = computed(() => {
    const messages = [];
    const selectedPackage = selectedPackages.value[0];
    if (!selectedPackage) return messages;
    messages.push(i18n.t('messages.cartPackageTypeRestriction'));
    if (selectedPackage.isCuratedDeal) {
      messages.push(i18n.t('messages.cartCurateRestriction'));
    } else if (selectedPackage.purchaseType === PurchaseTypesEnum.GUARANTEED) {
      messages.push(i18n.t('messages.cartPGRestriction'));
    } else if (selectedPackage.demographicTargetsOverridable) {
      messages.push(i18n.t('messages.cartNoDemographicsRestriction'));
    } else {
      messages.push(i18n.t('messages.cartHasDemographicsRestriction'));
    }
    return messages;
  });

  //
  // Store actions
  //
  const { updatePackageState } = mapActions();
  const {
    getOrganizationSubscriptions,
    deleteOrganizationSubscription,
    subscribeToOrganizationSubscriptions,
    getOrganizationDSPIDs,
    getExternalOrganizationDSPIDs,
    getCurators,
  } = mapOrgAppActions();

  //
  // Emits
  //
  const emit = defineEmits(['download', 'deletePackages', 'getPackages', 'publishPackages', 'unpublishPackages']);

  //
  // Functions
  //
  function newDeal() {
    router.push(newDealRoute.value).catch(() => {
      // Do nothing
    });
  }

  function confirmPackagesDeletion() {
    emit('deletePackages', selectedPackages.value);
  }

  function cancelPackagesDeletion() {
    isDeletePackagesModalVisible.value = false;
  }

  function promptForPackagesPublish() {
    isPublishPackagesModalVisible.value = true;
  }

  function confirmPackagesPublish() {
    const packs = selectedPackages.value.filter((x) => x.status === 'draft');
    packs.map((x) =>
      updatePackageState({
        package: x,
        value: PackageStatus.Published,
      }),
    );
    emit('publishPackages', packs);
  }

  function cancelPackagesPublish() {
    isPublishPackagesModalVisible.value = false;
  }

  function promptForPackagesUnpublish() {
    isUnpublishPackagesModalVisible.value = true;
  }

  function confirmPackagesUnpublish() {
    const packs = selectedPackages.value.filter((x) => x.status === 'upcoming' || x.status === 'live');
    packs.map((x) =>
      updatePackageState({
        package: x,
        value: x.status === 'upcoming' ? PackageStatus.Draft : PackageStatus.Retired,
      }),
    );
    emit('unpublishPackages', packs);
  }

  function cancelPackagesUnpublish() {
    isUnpublishPackagesModalVisible.value = false;
  }

  function onTableFiltersChanged(queryParams: QueryParams) {
    const sortByColumnNames = queryParams.sort != null ? queryParams.sort.replace(/ desc/gm, '') : '';
    if (
      queryParams.filterCollection.filters.some(
        (x) => x.type === 'FilterGroup' && x.filters.some((y) => y.property === 'name'),
      )
    ) {
      [filterText.value] = queryParams.filterCollection.filters.find(
        (x) => x.type === 'FilterGroup' && x.filters.some((y) => y.property === 'name'),
      ).filters[0].values;
    }

    reloadPackages(queryParams);

    if (viewType.value === DealsListViewType.TILES) {
      if (queryParams.sort != null && queryParams.sort !== '') {
        const sb = sortByOptions.value.find(
          (x) =>
            x.property === queryParams.sort.split(' ')[0] ||
            (x.orderBy != null &&
              (sortByColumnNames.includes(x.orderBy.join(',')) ||
                sortByColumnNames.includes(x.orderBy.reverse().join(',')))),
        );
        if (sb !== sortBy.value) {
          sortBy.value = sb;
        }
        const sd = sortDirectionOptions.value.find(
          (x) => x.value === (queryParams.sort.includes('desc') ? 'desc' : 'asc'),
        );
        if (sortDirection.value !== sd) {
          sortDirection.value = sd;
        }
      } else {
        sortBy.value = sortByOptions.value.find((x) => x.property === 'name');
        sortDirection.value = sortDirectionOptions.value.find((x) => x.value === 'asc');
      }
    }
  }

  async function reloadPackages(queryParams: QueryParams = null) {
    if (table.value === undefined) {
      setTimeout(reloadPackages, 100);
      return;
    }
    if (queryParams == null) queryParams = table.value.queryParams;
    queryParams.showLoader = false;

    // available is a boolean filter in the frontend, but should be send as a FilterGroup to the backend
    const availableFilter = queryParams.filterCollection.filters.find((x) => x.property === 'available');
    if (availableFilter) {
      const isAvailable = availableFilter.values[0];

      queryParams.filterCollection.filters = queryParams.filterCollection.filters.filter(
        (x) => x.property !== 'available',
      );

      const filterGroup = new FilterGroup(
        [
          new Filter('state', ['P'], isAvailable ? FilterComparisonOperator.EQUAL : FilterComparisonOperator.NOT_EQUAL),
          new Filter(
            'flight_to',
            [toServerDateTimeNoTimezone(DateTime.local().startOf('day'))],
            isAvailable ? FilterComparisonOperator.GREATER_THAN_OR_EQUAL : FilterComparisonOperator.LESS_THAN,
          ),
        ],
        isAvailable ? FilterLogicalOperator.AND : FilterLogicalOperator.OR,
      );

      queryParams.filterCollection.filters.push(filterGroup);
    }

    // Get all media owners and filter it with search term that matches organization name,
    // and pass the organization_id to get packages
    if (filterText.value != null && !isSupplyOrganization.value) {
      await waitForPendingQueries([mediaOwnersQuery]);
      const organizationIDs = mediaOwnersQuery.items.value
        .filter((mediaOwner) => mediaOwner.name.toLowerCase().includes(filterText.value))
        .map((mediaOwner) => mediaOwner.id);

      const { filters } = queryParams.filterCollection.filters.find((x) => x.type === 'FilterGroup') || {};

      if (organizationIDs.length > 0 && filters != null) {
        const newFilter = new Filter('organization_id', organizationIDs, FilterComparisonOperator.IN);
        filters.push(newFilter);
      }
    }

    if (props.isForDetailsPage) {
      queryParams.filterCollection.filters.push(new Filter('plan_id', [props.planID], FilterComparisonOperator.EQUAL));
    }

    if (isSidekickOrganization.value) {
      const arePurchaseTypeFiltersSet =
        queryParams.filterCollection.filters.filter((f) => !!findPath(f, 'property', 'purchase_type'))?.length > 0;

      // For sidekick, we cannot let the user see all the deals.
      // If the user hasn't set any filters for purchase_type, automatically add the default sidekick deal filters
      if (!arePurchaseTypeFiltersSet) {
        queryParams.filterCollection.filters.push(sidekickDealFilters.value);
      }
    }
    if (JSON.stringify(queryParams) === JSON.stringify(lastUsedQueryParams.value)) {
      return;
    }
    lastUsedQueryParams.value = Object.assign({}, queryParams);
    emit('getPackages', queryParams);
  }

  function onTableLoaded(childTable: typeof Table) {
    const previousTableData = table.value;
    table.value = childTable;
    if (previousTableData) {
      table.value.pageIndex = previousTableData.pageIndex;
      table.value.pageSize = previousTableData.pageSize;
      table.value.filterText = previousTableData.filterText;
      table.value.activeFilters = previousTableData.activeFilters;
      table.value.fireFiltersChanged();
    } else {
      onTableFiltersChanged(table.value.queryParams);
    }
    if (!lockPublisherSelection.value && selectedPackages.value.length > 0) {
      activeSelectionTable.value = table.value;
      activeSelectionTable.value.selectedItems = selectedPackages.value;
    }
  }

  // Clears all selections in the actively selected table
  function clearPackages() {
    activeSelectionTable.value.clearSelection();
  }

  // De-selects the package in the actively selected table
  function removePackage(p: Package) {
    activeSelectionTable.value.handleItemSelection(p, false);
  }

  async function getOrgSubscriptions() {
    const orgSubscriptions = await getOrganizationSubscriptions();
    const sub = orgSubscriptions.find((entry) => entry.organizationSubscriptionType === 'newdeal');
    if (sub === undefined) {
      newDealSubscription.value = null;
    } else {
      newDealSubscription.value = sub;
    }
  }

  async function subscribeToEmails() {
    await subscribeToOrganizationSubscriptions('newdeal');
    await getOrgSubscriptions();
  }

  async function unsubscribeToEmails() {
    await deleteOrganizationSubscription(newDealSubscription.value.id);
    await getOrgSubscriptions();
  }

  function closeModal() {
    isDeletePackagesModalVisible.value = false;
    isPublishPackagesModalVisible.value = false;
    isUnpublishPackagesModalVisible.value = false;
  }

  function onCuratedDealsListLoaded(listHasCuratedDeals = false) {
    hasCuratedDeals.value = listHasCuratedDeals;
  }

  function tileDownloadClicked() {
    table.value.isDownloadModalVisible = true;
  }

  function onDownload(cb) {
    const { queryParams } = table.value;
    emit('download', {
      callback: cb,
      queryParams,
    });
  }

  function matchColumn(property: string) {
    switch (property) {
      case 'deal_id':
        return 'dealID';
      case 'flight_from_min':
        return 'startDateMin';
      case 'flight_to_max':
        return 'endDateMax';
      case 'impressions':
        return 'impressionsDescription';
      case 'budget':
        return 'budgetDescription';
      case 'purchase_type':
        return 'purchaseTypeName';
      case 'modified_on_utc':
        return 'modifiedOnUtc';
      default:
        return property;
    }
  }

  function dspCustomQueryParams(values, comparisonOperator) {
    const isExclusion = comparisonOperator === FilterComparisonOperator.NOT_IN;
    const relationshipOperator = isExclusion ? 'all' : 'any';
    const normalizedComparisonOperator = Filter.getNormalizedComparisonOperator(comparisonOperator);
    const hasHivestackDSP = values.includes(-1) || values.includes('-1');
    const [dspTable, dspProperty] = Package.entityProperties.dspExchanges.sourceFilterProperty.split('.');
    const [buyerTable, buyerProperty] = Package.entityProperties.dspBuyers.sourceFilterProperty.split('.');

    const draftQuery = [];
    let draftValues = values;

    if (hasHivestackDSP) {
      // Exclusion - Either the external id or dsp id isn't null (Both being null would mean the DSP is Hivestack)
      const excludedQuery = `(${dspTable}.any(peer: peer.${dspProperty} ${FilterComparisonOperator.NOT_EQUAL} null) or ${buyerTable}.any(por: por.${buyerProperty} ${FilterComparisonOperator.NOT_EQUAL} null))`;
      // Inclusion - Both the external id and dsp id need to be null
      const includedQuery = `(${dspTable}.all(peer: peer.${dspProperty} ${FilterComparisonOperator.EQUAL} null) and ${buyerTable}.all(por: por.${buyerProperty} ${FilterComparisonOperator.EQUAL} null))`;
      // We want to remove the Hivestack DSP id (-1) from the values since the logic for exclusion/inclusion is already done.
      draftValues = draftValues.filter((val) => val != -1);
      draftQuery.push(comparisonOperator === FilterComparisonOperator.NOT_IN ? excludedQuery : includedQuery);
    }

    if (draftValues.length > 0) {
      draftQuery.push(
        `${dspTable}.${relationshipOperator}(peer: peer.${dspProperty} ${normalizedComparisonOperator} (${draftValues.join(',')}))`,
      );
    }

    const multiLogicalOperator = hasHivestackDSP && isExclusion ? LogicalOperator.AND : LogicalOperator.OR;
    return `(${draftQuery.join(` ${multiLogicalOperator} `)})`;
  }

  function buyerCustomQueryParams(values, comparisonOperator) {
    const isExclusion = comparisonOperator === FilterComparisonOperator.NOT_IN;
    const relationshipOperator = isExclusion ? 'all' : 'any';
    const normalizedComparisonOperator = Filter.getNormalizedComparisonOperator(comparisonOperator);
    const [buyerTable, buyerProperty] = Package.entityProperties.dspBuyers.sourceFilterProperty.split('.');

    return `${buyerTable}.${relationshipOperator}(por: por.${buyerProperty} ${normalizedComparisonOperator} (${values.join(',')}))`;
  }

  function curatedDealIDCustomQueryParams(values, comparisonOperator) {
    const searchText = comparisonOperator === FilterComparisonOperator.IN ? values.join("','") : values.join(',');
    let queryString = `package_children.any(p: p.deal_id ${comparisonOperator} '${searchText}')`;
    switch (comparisonOperator) {
      case FilterComparisonOperator.LIKE:
        queryString = `package_children.any(p: p.deal_id ${comparisonOperator} '%${searchText}%')`;
        break;
      case FilterComparisonOperator.STARTS_WITH:
        queryString = `package_children.any(p: p.deal_id ${FilterComparisonOperator.LIKE} '${searchText}%')`;
        break;
      case FilterComparisonOperator.IN:
        queryString = `package_children.any(p: p.deal_id ${comparisonOperator} ('${searchText}'))`;
        break;
      default:
        queryString = `package_children.any(p: p.deal_id ${comparisonOperator} '${searchText}')`;
        break;
    }
    return queryString;
  }

  function buyerFilterOptionsTransformation(options) {
    const resultOptions = [];
    const resultOptionsRaw = {};
    options.forEach((option) => {
      const dspName = option.dsp.name;
      const dspID = option.dsp.externalExchangeID ? option.dsp.externalExchangeID : -1;
      if (!resultOptionsRaw[dspName]) {
        resultOptionsRaw[dspName] = { id: dspID, name: dspName, children: [] };
        resultOptions.push(resultOptionsRaw[dspName]);
      }
      resultOptionsRaw[dspName].children.push(option);
    });
    return resultOptions;
  }

  function dispatchAction(action) {
    switch (action.type) {
      case 'publish-curated-deals':
        promptForPackagesPublish();
        break;
      case 'unpublish-curated-deals':
        promptForPackagesUnpublish();
        break;
      default:
        break;
    }
  }

  function buyFromDeals() {
    if (selectedPackages.value.length === 0) {
      return;
    }
    // In the case of curated deals, the array will only ever contain one package.
    const packageIDs = selectedPackages.value.map((x) => x.id).join(',');
    router.push(
      lockCuratedSelection.value
        ? `/lineitems/new/edit/package/${packageIDs}`
        : `/lineitems/new/edit/curated-package/${packageIDs}`,
    );
  }

  function onRequestedDealDetails(deal: Package) {
    const mutation = (change: Package) => (activeDeal.value = change);
    onDealPreviewChanged(deal, activeDeal.value, mutation);
  }

  //
  // Exposed functions
  //
  defineExpose({
    closeModal,
    reloadPackages,
    viewType,
  });

  //
  // Watches
  //

  watch(
    () => selectedPackages.value,
    (val) => {
      if (val.length === 0) {
        lockPublisherSelection.value = false;
        lockCuratedSelection.value = false;
        activeSelectionTable.value = null;
        return;
      }
      const firstPackage: Package = val[0];
      // Verify deal type and disable selection accordingly.
      if (firstPackage.isCuratedDeal && curatedDealsListRef.value) {
        activeSelectionTable.value = curatedDealsListRef.value.table;
        lockPublisherSelection.value = true;
      } else {
        activeSelectionTable.value = table.value;
        lockCuratedSelection.value = true;
      }
    },
  );

  // Since the CD tables are handled externally (unlike publisher tables), we need to update the active table accordingly.
  watch(
    () => curatedDealsListRef.value?.table,
    (val) => {
      if (!val) return;
      if (lockPublisherSelection.value) {
        activeSelectionTable.value = val;
      }
    },
  );

  watch(
    () => sortDirection.value,
    (option) => {
      tableSortAscending.value = option == null || option.value === 'asc';
    },
  );

  watch(
    () => sortBy.value,
    (option) => {
      let col = null;
      if (option != null) {
        Object.keys(columns.value).forEach((x) => {
          if (columns.value[x].property === option.property) {
            col = columns.value[x];
          }
        });
      }
      tableSortColumn.value = col;
    },
  );

  watch(
    () => pageIndex.value,
    (val) => {
      table.value?.setPageIndex(val);
    },
  );
  watch(
    () => pageSize.value,
    (val) => {
      table.value?.setPageSize(val);
    },
  );

  //
  // Lifecycle hooks
  //
  onBeforeMount(async () => {
    if (isDemandOrganization.value) {
      fixedFilters.value = [
        new Filter(
          'flight_to',
          [toServerDateTimeNoTimezone(DateTime.local().startOf('day'))],
          FilterComparisonOperator.GREATER_THAN_OR_EQUAL,
        ),
        new Filter('is_external', [false]),
        new Filter('state', [PackageStatus.Published]),
      ];
    } else if (isSupplyOrganization.value) {
      fixedFilters.value = [new Filter('is_external', [false])];
    }
  });

  onBeforeMount(() => {
    // Sort by start date descending by default CAM-476
    sortBy.value = sortByOptions.value.find((x) => x.property === 'start_date');
    sortDirection.value = sortDirectionOptions.value.find((x) => x.value === 'desc');
  });

  onMounted(async () => {
    if (!organizationDSPIDs.value.length) {
      await getOrganizationDSPIDs();
    }
    if (!externalOrganizationDSPIDs.value.length) {
      await getExternalOrganizationDSPIDs();
    }
    if (isDemandOrganization.value) {
      viewType.value = DealsListViewType.TILES;
      await getOrgSubscriptions();
      GlobalEventBus.$on(GlobalEvents.orgSubscriptionsChanged, getOrgSubscriptions);
    } else if (isSupplyOrganization.value) {
      viewType.value = DealsListViewType.LIST;
      await waitForPendingQueries([externalExchangesQuery]);
    } else if (isExchange.value) {
      viewType.value = DealsListViewType.LIST;
    }
    if (isDemandOrganization.value && allCuratorOrganizations.value.length === 0) {
      await getCurators();
    }
    if (mediaTypes.value == null || mediaTypes.value.length === 0) {
      mediaTypes.value = await runQueryInScope(useMediaTypes);
    }
    isReady.value = true;
  });

  onUnmounted(() => {
    if (isDemandOrganization.value) {
      GlobalEventBus.$off(GlobalEvents.orgSubscriptionsChanged);
    }
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  @use '@/styles/variables' as *;

  .drawer-content {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
  }

  .view-type-buttons {
    float: right;
    display: flex;
    flex-direction: row-reverse;
    margin-bottom: 1rem;
    font-size: 1.4rem;
  }

  .page-size-container {
    display: flex;
    flex-direction: row-reverse;

    .multiselect {
      flex-basis: auto;
      max-width: 19rem;
    }
  }

  #app {
    .packages-details-container {
      .pane-content.filters {
        display: flex;
        flex-direction: row;
        width: 100%;
        padding-top: 0.8rem;

        section {
          width: 100%;
        }

        > * {
          margin-right: 1rem;
          width: 100%;
        }

        .btn {
          margin-left: 0.8rem;
        }

        .input-container.daterangepicker-input {
          width: 21rem !important;
          margin-right: 0.8rem;
        }
      }
    }
  }

  @media screen and (max-width: 1200px) {
    #app {
      .pane-content.filters {
        flex-direction: column;

        > div {
          margin-bottom: 1rem;
        }
      }
    }
  }

  .deal-tiles-grid {
    display: grid;
    grid-gap: 1rem;
    grid-template-columns: repeat(auto-fit, minmax(30rem, 1fr));
  }

  .curated-deal-tiles-grid {
    margin-top: 1.6rem;
    margin-bottom: 3.2rem;
    display: grid;
    grid-gap: 3rem;
    grid-template-columns: repeat(auto-fit, minmax(40rem, 1fr));
  }

  .deals-page-container {
    display: flex;
    flex-direction: row;
    padding: 0 !important;
    transition: all 0.4s ease;
  }

  .deals-table-wrapper {
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 2rem;

    &.details-view {
      padding: 0;
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 1s ease;
  }
  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }
</style>
